<template>
  <div class="page-wrapper">
    <div id="app-container">
      <div v-if="isLoading" class="loading-container">
        <div class="spinner"></div>
        <p>Loading Genomic Region for <strong>{{ geneId }}</strong>...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <h2><span class="material-symbols-outlined">error</span> Failed to Load Data</h2>
        <p>{{ error }}</p>
      </div>

      <div v-else class="viewer-content">
        <header class="viewer-main-header">
          <h1>Genomic Region Viewer</h1>
          <p>
            An interactive visualization of genes and genetic variants within a specific chromosomal region.
          </p>
          <br>
          <h5 class="header-text" style="color: black;"> 💡 Click a variant to open its details page. Scroll to zoom, and click and drag to pan across the region</h5>            
        </header>

        <div class="plot-header">
          <div class="title-group">
            <h2>Chromosome {{ region.chrom.replace(/^chr/i, '') }}</h2>
            <span>Region {{ new Intl.NumberFormat().format(region.start) }} - {{ new Intl.NumberFormat().format(region.end) }}</span>
          </div>
          <div id="controls">
            <button id="info-btn" title="Plot Information">
              <span class="material-symbols-outlined">info</span>
            </button>
            <button id="reset-btn" title="Reset View">
              <span class="material-symbols-outlined">autorenew</span>
            </button>
          </div>
        </div>

        <div id="viewer-container" ref="viewerEl">
        </div>
      </div>
    </div>
    <div id="tooltip"></div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import * as d3 from 'd3';
import Swal from 'sweetalert2';


// --- STATE MANAGEMENT ---
const isLoading = ref(true);
const error = ref(null);
const viewerEl = ref(null);
const geneId = ref(null);
const region = ref(null);
const trackData = ref([]);
const variantData = ref([]);

const route = useRoute();


const showPlotInfo = () => {
  Swal.fire({
    title: '<h6 style="font-family: Ubuntu, sans-serif; font-size: 24px;">Genomic Viewer Guide</h6>',
    html: `<div style="text-align: left; font-size: 15px; font-family: Inter, sans-serif; line-height: 1.6;">
        <p>This interactive viewer displays genes and genetic variants for a specific genomic region. Use the following controls to explore the data:</p>
        <ul style="list-style-position: inside; padding-left: 0; margin-top: 20px; margin-bottom: 20px;">
            <li style="margin-bottom: 12px;"><strong>Zoom:</strong> Use your mouse scroll wheel to zoom in and out.</li>
            <li style="margin-bottom: 12px;"><strong>Pan:</strong> Click and drag the plot to move left or right along the chromosome.</li>
            <li style="margin-bottom: 12px;"><strong>Reset:</strong> Click the reset button (<span class="material-symbols-outlined" style="font-size: 1.1em; vertical-align: -0.2em;">autorenew</span>) to return to the initial view.</li>
        </ul>
        <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;" />
        <p>The plot is divided into two main panels:</p>
        <ul style="list-style-position: inside; padding-left: 0;">
             <li style="margin-bottom: 12px;"><strong>Variant Plot:</strong> The red dots in the top panel represent genetic variants. The vertical position (Y-axis) corresponds to the selection score <strong>|X|</strong>. Hover over a dot for details, or click it to open the variant's dedicated page.</li>
            <li style="margin-bottom: 12px;"><strong>Gene Track:</strong> The blue bars in the bottom panel represent genes. When you zoom in, gene names will appear. You can also hover over any gene to see its exact coordinates. Genes are arranged in non-overlapping rows automatically.</li>
        </ul>
    </div>`,
    customClass: {
          title: "swal-title",
          htmlContainer: "swal-text-container",
          confirmButton: "swal-confirm-button",
    },
    confirmButtonColor: "#A51C30",
    confirmButtonText: "Got it!",
    position: 'center',
    icon: 'info',
    iconColor: '#A51C30',
    width: 800
  });
};

// --- DATA FETCHING ---
const fetchRegionData = async (id) => {
  isLoading.value = true;
  error.value = null;
  try {
    const response = await axios.create({
      baseURL: process.env.VUE_APP_ROOT_API,
      headers: { Authorization: `Bearer ${localStorage.getItem('ages_hms_access_token')}` },
    }).post(`/api/fetch-region-data`, { gene_id: id });

    if (response.data.error) {
      error.value = response.data.error;
    } else {
      const originalRegion = response.data.region;
      region.value = {
        ...originalRegion, 
        start: originalRegion.start - 250000,
        end: originalRegion.end + 250000
      };
      trackData.value = response.data.tracks;
      variantData.value = response.data.variants || [];
    }
  } catch (err) {
    error.value = "Failed to load genomic data.";
    console.error(err);
  } finally {
    isLoading.value = false;
  }
};

// --- WATCHERS ---
watch(() => route.params.geneId, (newGeneId) => {
  if (newGeneId) {
    geneId.value = newGeneId;
    fetchRegionData(newGeneId);
  }
}, { immediate: true });


watch(trackData, (newData) => {
  if (!isLoading.value && viewerEl.value && newData && region.value) {
    initializeViewer(newData, variantData.value, region.value);
  }
}, { flush: 'post' });

/**
 * Assigns a 'row' property to each gene to prevent visual overlap.
 */
const assignGeneRows = (genes) => {
  if (!genes || genes.length === 0) return { processedGenes: [], numRows: 0 };
  const sortedGenes = [...genes].sort((a, b) => a.start - b.start);
  const lanes = [];

  sortedGenes.forEach(gene => {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (gene.start > lanes[i]) {
        lanes[i] = gene.end;
        gene.row = i;
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push(gene.end);
      gene.row = lanes.length - 1;
    }
  });
  return { processedGenes: sortedGenes, numRows: lanes.length };
};


// --- D3 PLOT RENDERING LOGIC ---
const initializeViewer = (tracks, variants, regionData) => {
  const container = d3.select(viewerEl.value);
  container.selectAll("svg").remove();
  const tooltip = d3.select("#tooltip").style("opacity", 0);

  const geneTracks = tracks.filter(t => t.type === 'gene');
  const { processedGenes, numRows } = assignGeneRows(geneTracks);

  // --- 1. LAYOUT & DIMENSIONS ---
  const config = {
    margin: { top: 10, right: 20, bottom: 40, left: 70 },
    width: 920,
    variantPlotHeight: 250,
    genePanelPaddingTop: 40,
    geneTrackHeight: 25,
    geneTrackPadding: 5,
    geneLabelOffset: 5
  };

  const genePanelHeight = Math.max(40, numRows * (config.geneTrackHeight + config.geneTrackPadding));
  const totalHeight = config.variantPlotHeight + config.genePanelPaddingTop + genePanelHeight;

  const svgRoot = container.append("svg").attr("class", "viewer-svg")
      .attr("width", config.width + config.margin.left + config.margin.right)
      .attr("height", totalHeight + config.margin.top + config.margin.bottom);
  
  const svg = svgRoot.append("g").attr("transform", `translate(${config.margin.left},${config.margin.top})`);
  
  svg.append("defs").append("clipPath").attr("id", "clip-region")
      .append("rect").attr("width", config.width).attr("height", totalHeight);
      
  const chartArea = svg.append("g").attr("clip-path", "url(#clip-region)");
  
  // --- 2. SCALES ---
  const xScale = d3.scaleLinear().domain([regionData.start, regionData.end]).range([0, config.width]);
  const maxVariantY = d3.max(variants, d => d.yValue) || 1;
  const yScaleVariant = d3.scaleLinear().domain([0, maxVariantY * 1.1]).range([config.variantPlotHeight, 0]);

  // --- 3. AXES & LABELS ---
  const xAxis = d3.axisBottom(xScale).tickFormat(d3.format(",.0f"));
  const xAxisGroup = svg.append("g").attr("class", "x-axis").attr("transform", `translate(0, ${totalHeight})`).call(xAxis);

  // Y-Axis for Variant Plot
  const variantYAxis = d3.axisLeft(yScaleVariant).ticks(5).tickFormat(d3.format(".2f"));
  svg.append("g").attr("class", "y-axis").attr("transform", `translate(0, 0)`).call(variantYAxis);
  svg.append("text").attr("class", "y-axis-label")
    .attr("transform", "rotate(-90)").attr("y", -config.margin.left + 20)
    .attr("x", -(config.variantPlotHeight / 2)).style("text-anchor", "middle")
    .text("Selection (|X|)");
  
  // Y-Axis Label for Gene Panel
  const genePanelY = config.variantPlotHeight + config.genePanelPaddingTop;
  svg.append("text").attr("class", "y-axis-label")
    .attr("transform", "rotate(-90)").attr("y", -config.margin.left + 20)
    .attr("x", -(genePanelY + genePanelHeight / 2)).style("text-anchor", "middle")
    .text("Genes");

  // --- 4. PANELS & INTERACTIVITY SETUP ---
  const variantGroup = chartArea.append("g");
  const genePanel = chartArea.append("g").attr("transform", `translate(0, ${genePanelY})`);

  genePanel.append("rect")
    .attr("class", "gene-panel-bg")
    .attr("width", config.width)
    .attr("height", genePanelHeight)
    .attr("fill", "white");

  const tooltipMove = (event) => tooltip.style("left", `${event.pageX + 15}px`).style("top", `${event.pageY - 28}px`);
  const tooltipHide = () => tooltip.style("opacity", 0);
  const trackTooltipShow = (event, d) => tooltip.style("opacity", 1).html(`<b>${d.name}</b><br>${d.start.toLocaleString()} - ${d.end.toLocaleString()}`);
  const variantTooltipShow = (event, d) => tooltip.style("opacity", 1).html(`<b>${d.id}</b><br><b>${d.rsid}</b><br>Pos: ${d.pos.toLocaleString()}<br>|X|: ${d.yValue.toFixed(3)}<br><i>Click to open</i>`);
  
  let currentTransform = d3.zoomIdentity;
  let initialTransform = d3.zoomIdentity;

  // --- 5. UPDATE FUNCTION (RENDERS ALL ELEMENTS) ---
  function update() {
    const newXScale = currentTransform.rescaleX(xScale);
    xAxisGroup.call(xAxis.scale(newXScale));
    
    // Render Variants
    variantGroup.selectAll(".variant").data(variants, d => d.id)
      .join("circle")
        .attr("class", "variant")
        .attr("r", 4)
        .attr("fill", "#1e1e1ec4")
        .on("mouseover", variantTooltipShow).on("mousemove", tooltipMove).on("mouseout", tooltipHide)
        .on("click", (event, d) => window.open(`/#/variant/${d.id}`, '_blank'))
        .attr("cx", d => newXScale(d.pos))
        .attr("cy", d => yScaleVariant(d.yValue));

    // --- Render Gene Tracks ---
const geneGroups = genePanel.selectAll(".gene-group")
  .data(processedGenes, d => d.name)
  .join(
    // Create the group and its contents for new data
    enter => {
      const g = enter.append("g")
        .attr("class", "gene-group track")
        .on("mouseover", trackTooltipShow)
        .on("mousemove", tooltipMove)
        .on("mouseout", tooltipHide);

      // The main horizontal line
      g.append("line").attr("class", "gene-line");

      // The vertical start tick: |
      g.append("line").attr("class", "gene-tick gene-start-tick");

      // The vertical end tick: |
      g.append("line").attr("class", "gene-tick gene-end-tick");

      // The gene name text
      g.append("text").attr("class", "gene-label-text");
      
      return g;
    }
  );

// --- Update positions for all gene groups (both new and existing) ---

// Position the entire group vertically based on its assigned row
geneGroups
  .attr("transform", d => `translate(0, ${d.row * (config.geneTrackHeight + config.geneTrackPadding)})`);

// Update the main horizontal line
geneGroups.select(".gene-line")
  .attr("x1", d => newXScale(d.start))
  .attr("x2", d => newXScale(d.end))
  .attr("y1", config.geneTrackHeight / 2)
  .attr("y2", config.geneTrackHeight / 2);

// Update the start tick
geneGroups.select(".gene-start-tick")
  .attr("x1", d => newXScale(d.start))
  .attr("x2", d => newXScale(d.start))
  .attr("y1", 0)
  .attr("y2", config.geneTrackHeight);
  
// Update the end tick
geneGroups.select(".gene-end-tick")
  .attr("x1", d => newXScale(d.end))
  .attr("x2", d => newXScale(d.end))
  .attr("y1", 0)
  .attr("y2", config.geneTrackHeight);
  
// Update the text label
geneGroups.select(".gene-label-text")
  .attr("x", d => (newXScale(d.start) + newXScale(d.end)) / 2) // Center the text
  .attr("y", config.geneTrackHeight / 2)
  .attr("dy", "-0.35em") // Position it slightly above the line
  .text(d => d.name)
  .style("display", d => (newXScale(d.end) - newXScale(d.start) > d.name.length * 8) ? "inline" : "none");
  }
  
const zoom = d3.zoom()
  .scaleExtent([1.25, 2000])
  .translateExtent([[0, 0], [config.width, totalHeight]])
  .on("zoom", (event) => {
    const { k, x } = event.transform;

    const minTranslateX = config.width * (1 - k);
    const maxTranslateX = 0;

    event.transform.x = Math.max(minTranslateX, Math.min(x, maxTranslateX));

    currentTransform = event.transform;
    update();
  });
  svgRoot.call(zoom);
  
  d3.select("#reset-btn").on("click", () => svgRoot.transition().duration(100).call(zoom.transform, initialTransform));
  d3.select("#info-btn").on("click", () => showPlotInfo());

  // Set initial zoom to a padded view
  const padding = 800000;
  const initialDomainStart = regionData.start + padding;
  const initialDomainEnd = regionData.end - padding;
  if (initialDomainStart < initialDomainEnd) {
    const k = config.width / (xScale(initialDomainEnd) - xScale(initialDomainStart));
    const tx = -xScale(initialDomainStart) * k;
    initialTransform = d3.zoomIdentity.translate(tx, 0).scale(k);
    svgRoot.call(zoom.transform, initialTransform);
  } else {
    update();
  }
};
</script>

<style>
/* --- FONT & ICON IMPORTS --- */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&family=Ubuntu:wght@400;500;700&display=swap');
@import url("https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200");

/* --- MAIN LAYOUT & WRAPPER --- */
.page-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100vh;
  width: 100%;
  background-color: #f8f9fa;
  padding: 2rem 1rem;
  font-family: 'Inter', sans-serif;
}

#app-container {
  width: 100%;
  max-width: 1100px;
  margin-top: 80px;
  margin-bottom: 50px;
  background-color: #ffffff;
  border-radius: 10px;
  border: 1px solid #1e1e1e;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.viewer-content {
  display: flex;
  flex-direction: column;
}

/* --- HEADERS --- */
.viewer-main-header {
  padding: 2rem 2.5rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}
.viewer-main-header h1 {
  font-family: 'Ubuntu', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 0.5rem 0;
}
.viewer-main-header p {
  font-size: 1rem;
  color: #6c757d;
  max-width: 80ch;
  line-height: 1.6;
  margin: 0;
}

.plot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2.5rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}
.title-group h2 {
  font-family: 'Ubuntu', sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
  color: #A51C30;
  margin: 0;
}
.title-group span {
  font-size: 0.9rem;
  color: #495057;
}

/* --- CONTROLS --- */
#controls {
  display: flex;
  gap: 0.75rem;
}
#controls button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background-color: #ffffff;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
#controls button:hover {
  background-color: #A51C30;
  border-color: #A51C30;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(165, 28, 48, 0.2);
}
#controls button .material-symbols-outlined {
  font-size: 20px;
}

/* --- D3 VIEWER AREA --- */
#viewer-container {
  display: flex;
  justify-content: center;
  padding: 1rem 1.5rem 1.5rem 1.5rem;
}
.viewer-svg {
  cursor: grab;
  background-color: #ffffff;
}
.viewer-svg:active {
  cursor: grabbing;
}

/* --- D3 ELEMENT STYLES --- */
.track {
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.2s;
  rx: 2;
  ry: 2;
}
.track:hover {
  opacity: 1;
}

.variant {
  opacity: 0.8;
  cursor: pointer;
  transition: opacity 0.2s, stroke-width 0.2s;
}
.variant:hover {
  opacity: 1;
  stroke: #A51C30;
  stroke-width: 2px;
}

.gene-line, .gene-tick {
  stroke: #D16103;
  stroke-width: 1.5px;
  pointer-events: none; /* Let the parent group handle mouse events */
}

.gene-label-text {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500;
  fill: #D16103;
  text-anchor: middle;
  pointer-events: none;
}

/* --- AXIS AND LABEL STYLES --- */
.y-axis-label {
  font-family: 'Ubuntu', sans-serif;
  font-size: 13px;
  font-weight: 500;
  fill: #6c757d;
  text-anchor: middle;
}
.x-axis .domain, .y-axis .domain {
  stroke: #adb5bd;
}
.x-axis .tick line, .y-axis .tick line {
  stroke: #e9ecef;
}
.x-axis .tick text, .y-axis .tick text {
  font-size: 11px;
  fill: #495057;
  font-family: 'Inter', sans-serif;
}

/* --- LOADING & ERROR STATES --- */
.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 500px;
  padding: 2rem;
  text-align: center;
  color: #495057;
}
.error-container h2 {
  color: #A51C30;
  font-family: 'Ubuntu', sans-serif;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.error-container .material-symbols-outlined {
  font-size: 1.5em;
}
.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #e9ecef;
  border-top-color: #A51C30;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* --- SWEETALERT2 & TOOLTIP --- */
.swal-confirm-button {
  background-color: #A51C30 !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-family: 'Inter', sans-serif !important;
  font-weight: 500 !important;
  padding: 10px 24px !important;
  box-shadow: 0 2px 10px rgba(165, 28, 48, 0.2) !important;
  transition: background-color 0.2s !important;
}
.swal-confirm-button:hover {
    background-color: #841626 !important;
}
.swal-text-container {
  text-align: left;
}

#tooltip {
  position: absolute;
  background-color: white;
  color: black;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 1000;
  line-height: 1.5;
  border: 1px solid black;
}
#tooltip b {
  color: black;
}
#tooltip i {
  color: black;
}

.header-text {
  background-color: #d7dbdb;
  padding: 5px;
  width: 650px;
  font-weight: normal;
  border-radius: 4px;
  margin-right: 10px;
}

</style>